import { useCallback, useEffect, useRef } from "react";

import { hardRedirect } from "App/routes/navigation";
import { checkSessionRequest, isClientLoggingOut, kickOut } from "Services/AuthService";
import { getLogger } from "Services/LoggingService";
import { isUnauthenticatedResponse } from "Services/util/NetworkUtilService";
import useEventListener from "Utils/useEventListener/useEventListener";

import { getCookieReduxBrandDrift } from "./brandSessionDrift";
import { regalAuthProfileExists } from "./RegalAuthContext";

const logger = getLogger("useSessionValidityCheck");

const AUTH_COOKIE_CHECK_INTERVAL = 3000;

/**
 * On tab focus: if cookie vs Redux brand disagree, hard-navigate home; else ping backend for session validity.
 */
export function useSessionValidityCheck() {
    const documentRef = useRef(document);
    const handler = useCallback(async () => {
        if (document.visibilityState === "visible" && !isClientLoggingOut()) {
            // Divergent brand first so we avoid an unnecessary session request when we will hard-redirect anyway.
            if (getCookieReduxBrandDrift()) {
                logger.log("Session cookie brand differs from Redux, redirecting to home");
                hardRedirect("/");
                return;
            }
            // we purposefully not use the fetch ponyfill to handle the unauthenticated scenario differently
            const response = await checkSessionRequest();
            if (isUnauthenticatedResponse(response.status) && !isClientLoggingOut()) {
                logger.log("User's session became invalid, kicking user to login", {
                    statusCode: response.status,
                });
                await kickOut();
            }
        }
    }, []);

    useEventListener("visibilitychange", handler, documentRef);

    useEffect(() => {
        const intervalId = setInterval(async () => {
            if (isClientLoggingOut()) {
                return;
            }
            if (getCookieReduxBrandDrift()) {
                logger.log("Session cookie brand differs from Redux, redirecting to home");
                hardRedirect("/");
                return;
            }
            // check if auth cookie is there (profile is loaded from cookie)
            if (!regalAuthProfileExists()) {
                logger.log("User's cookie is not present in browser, kicking user to login");
                await kickOut();
            }
        }, AUTH_COOKIE_CHECK_INTERVAL);
        return () => clearInterval(intervalId);
    }, []);
}
